import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, RefObject } from "react";

type MenuPosition = { top: number; right: number };

const MENU_OFFSET_PX = 6;

// Keys that move focus between items, as an index from the focused one.
const NAV_KEYS: Readonly<Record<string, (at: number, count: number) => number>> = {
  ArrowDown: (at, count) => (at + 1) % count,
  ArrowUp: (at, count) => (at - 1 + count) % count,
  Home: () => 0,
  End: (_at, count) => count - 1,
};

export type AnchoredMenu = {
  open: boolean;
  toggle: () => void;
  close: (refocus: boolean) => void;
  triggerRef: RefObject<HTMLButtonElement>;
  menuRef: RefObject<HTMLDivElement>;
  setItemRef: (index: number) => (el: HTMLButtonElement | null) => void;
  // Fixed coordinates once placed; hidden until then so the menu never
  // flashes at the viewport origin.
  menuStyle: CSSProperties;
  onMenuKeyDown: (evt: ReactKeyboardEvent<HTMLDivElement>) => void;
};

// A menu portalled out of its trigger's clipping ancestors: placed below the
// trigger, right-aligned to it, flipped above when it would run off the
// viewport. Focus moves into it on open.
export const useAnchoredMenu = (): AnchoredMenu => {
  const [open, setOpen] = useState<boolean>(false);
  const [pos, setPos] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const close = useCallback((refocus: boolean): void => {
    setOpen(false);
    setPos(null);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const place = useCallback((): void => {
    const trigger = triggerRef.current;
    if (trigger === null) return;
    const r = trigger.getBoundingClientRect();
    const height = menuRef.current?.getBoundingClientRect().height ?? 0;
    let top = r.bottom + MENU_OFFSET_PX;
    if (height > 0 && top + height > window.innerHeight) {
      top = Math.max(MENU_OFFSET_PX, r.top - MENU_OFFSET_PX - height);
    }
    const right = window.innerWidth - r.right;
    setPos((prev) => (prev?.top === top && prev.right === right ? prev : { top, right }));
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, pos, place]);

  // Only once placed: a visibility:hidden element can't take focus.
  // preventScroll, because any scroll closes the menu.
  const placed = pos !== null;
  useEffect(() => {
    if (open && placed) itemRefs.current[0]?.focus({ preventScroll: true });
  }, [open, placed]);

  useDismissal(open, close, triggerRef, menuRef);

  return {
    open,
    toggle: () => (open ? close(false) : setOpen(true)),
    close,
    triggerRef,
    menuRef,
    setItemRef: (index) => (el) => {
      itemRefs.current[index] = el;
    },
    menuStyle: pos === null ? { visibility: "hidden" } : { top: pos.top, right: pos.right },
    onMenuKeyDown: (evt) => moveFocus(evt, itemRefs.current, close),
  };
};

// Escape (from anywhere, not just inside the menu: a pointer user who opened
// it never moved focus off the trigger), an outside click, a scroll or a
// resize closes the menu.
const useDismissal = (
  open: boolean,
  close: (refocus: boolean) => void,
  triggerRef: RefObject<HTMLButtonElement>,
  menuRef: RefObject<HTMLDivElement>,
): void => {
  useEffect(() => {
    if (!open) return;
    const onPointer = (evt: MouseEvent): void => {
      const target = evt.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      close(false);
    };
    const onKey = (evt: KeyboardEvent): void => {
      if (evt.key === "Escape") close(true);
    };
    const onViewportChange = (): void => close(false);
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
    };
  }, [open, close, triggerRef, menuRef]);
};

// Arrow/Home/End move between items; Tab leaves the menu by closing it.
const moveFocus = (
  evt: ReactKeyboardEvent<HTMLDivElement>,
  itemEls: ReadonlyArray<HTMLButtonElement | null>,
  close: (refocus: boolean) => void,
): void => {
  if (evt.key === "Tab") {
    evt.preventDefault();
    close(true);
    return;
  }
  const next = NAV_KEYS[evt.key];
  if (next === undefined) return;
  evt.preventDefault();
  const items = itemEls.filter((el): el is HTMLButtonElement => el !== null);
  if (items.length === 0) return;
  const at = items.findIndex((el) => el === document.activeElement);
  items[next(at, items.length)]?.focus({ preventScroll: true });
};
